import React, { Suspense, useState } from 'react';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Sphere, MeshDistortMaterial } from '@react-three/drei';
import { FaHtml5, FaCss3Alt, FaReact, FaNodeJs, FaVideo, FaServer } from 'react-icons/fa';
import { SiMongodb, SiExpress, SiNextdotjs, SiTailwindcss, SiMarkdown, SiRedux, 
         SiFirebase, SiAmazonaws, SiGit, SiSocketdotio, SiPostman, 
         SiVisualstudiocode, SiWebstorm, SiOpenai, SiVercel, SiDigitalocean,SiCloudflare, SiDocker,
         SiRender, SiApache, SiNetlify, SiBootstrap, SiWindows, SiLinux, SiCpanel, SiFigma, 
          SiGithub, SiCircleci, SiJsonwebtokens, SiJira, SiFramer, SiThreedotjs, SiTestinglibrary, SiGooglechrome, SiFirefox, SiMicrosoftedge,
          SiGnubash, SiNpm, SiYarn } from 'react-icons/si';
import { AiOutlineApi } from 'react-icons/ai';
import { GoCode, GoCodeReview, GoShieldCheck, GoLightBulb} from 'react-icons/go';
import SEO from '../components/SEO/SEO';
import { AnimatePresence } from 'framer-motion';

const getIconColor = (skillName) => {
  switch (skillName) {
    case 'React':
      return 'text-blue-500'; // For React, use blue color
    case 'Node.js':
      return 'text-green-500'; // For Node.js, use green color
    case 'MongoDB':
      return 'text-green-600'; // For MongoDB, use green color
    // Add more cases as needed for other skills
    default:
      return 'text-white'; // Default color
  }
};


const ParticlesSphere = () => (
  <Sphere args={[1, 100, 200]} scale={2.5}>
    <MeshDistortMaterial
      color="#4f46e5"
      attach="material"
      distort={0.5}
      speed={1.5}
      roughness={0.2}
      metalness={0.8}
    />
  </Sphere>
);

const skillsData = {
  frontend: [
    { name: 'HTML', level: 95, icon: <FaHtml5 />, description: 'Structuring web content effectively', color: '#E34F26' },
    { name: 'CSS', level: 95, icon: <FaCss3Alt />, description: 'Designing responsive and modern UIs', color: '#1572B6' },
    { name: 'React.js', level: 90, icon: <FaReact />, description: 'Building scalable single-page applications', color: '#61DAFB' },
    { name: 'Next.js', level: 80, icon: <SiNextdotjs />, description: 'Server-side rendering & static generation', color: '#000000' },
    { name: 'Tailwind CSS', level: 90, icon: <SiTailwindcss />, description: 'Utility-first CSS framework', color: '#06B6D4' },
    { name: 'Redux Toolkit', level: 80, icon: <SiRedux />, description: 'State management for React apps', color: '#764ABC' },
    { name: 'Zustand', level: 80, description: 'Simplified state management for React apps', color: '#000000' },
    { name: 'Bootstrap', level: 90, icon: <SiBootstrap />, description: 'Responsive, mobile-first CSS framework', color: '#7952B3' },
    { name: 'Material UI', level: 80, description: 'React UI framework with pre-designed components', color: '#0081CB' },
    { name: 'Markdown', level: 90, icon: <SiMarkdown />, description: 'Writing and formatting Markdown for documentation and content', color: '#3178C6' },
    { name: 'Framer Motion', level: 75, icon: <SiFramer />, description: 'Declarative animations & gestures for React', color: '#0055FF' },
    { name: 'GSAP', level: 75, description: 'High-performance animations for web apps', color: '#88CE02' },
    { name: 'Three.js', level: 70, icon: <SiThreedotjs />, description: 'Creating 3D graphics and interactive experiences', color: '#000000' }
  ],
  

  backend: [
    { name: 'Node.js', level: 90, icon: <FaNodeJs />, description: 'Server-side JavaScript runtime', color: '#339933' },
    { name: 'Express.js', level: 90, icon: <SiExpress />, description: 'Web application framework', color: '#000000' },
    { name: 'REST API', level: 90, icon: <AiOutlineApi />, description: 'RESTful architecture & best practices', color: '#0096FF' },
    { name: 'Socket.IO', level: 80, icon: <SiSocketdotio />, description: 'Real-time, bi-directional communication', color: '#010101' },
    { name: 'WebRTC', level: 70, icon: <FaVideo />, description: 'Real-time communication between browsers', color: '#FF4500' },
  ],

  databases: [
    { name: 'MongoDB', level: 90, icon: <SiMongodb />, description: 'NoSQL database & aggregation pipelines', color: '#47A248' },
    { name: 'Firebase', level: 90, icon: <SiFirebase />, description: 'Backend-as-a-Service (BaaS) providing real-time database, authentication, and cloud functions', color: '#FFCA28' }

  ],
  security: [
    { name: 'JWT', level: 90, icon: <SiJsonwebtokens />, description: 'Secure authentication using JSON Web Tokens', color: '#FFB300' },
    { name: 'bcrypt', level: 85, description: 'Password hashing for secure authentication', color: '#4A90E2' },
    { name: 'Helmet', level: 90, description: 'Secure HTTP headers to protect Express apps', color: '#0086D1' },
    { name: 'xss-clean', level: 85, description: 'Prevents Cross-Site Scripting (XSS) attacks', color: '#E63946' },
    { name: 'hpp', level: 80, description: 'Protects against HTTP Parameter Pollution attacks', color: '#6A0572' },
    { name: 'express-mongo-sanitize', level: 85, description: 'Prevents NoSQL injection attacks in MongoDB', color: '#4CAF50' },
    { name: 'express-rate-limit', level: 90, description: 'Rate limiting to prevent brute-force attacks', color: '#FF6F00' },
    { name: 'CORS', level: 80, description: 'Controls Cross-Origin Resource Sharing policies', color: '#9C27B0' },
    { name: 'csurf', level: 85, description: 'Protects against Cross-Site Request Forgery (CSRF) attacks', color: '#FF4081' },
    { name: 'dotenv', level: 95, description: 'Manages environment variables securely', color: '#00C853' }
  ],

  deployment: [
    { name: 'AWS', level: 75, icon: <SiAmazonaws />, description: 'Cloud infrastructure & services', color: '#FF9900' },
    { name: 'Netlify', level: 90, icon: <SiNetlify />, description: 'Deploy Front-End', color: '#326CE5' },
    { name: 'Vercel', level: 85, icon: <SiVercel />, description: 'Frontend deployment & serverless functions', color: '#000000' },
    { name: 'cPanel', level: 90, icon: <SiCpanel />, description: 'Web hosting control panel', color: '#5C9E8D' },
    { name: 'aaPanel', level: 90, icon: <FaServer />, description: 'Lightweight web hosting control panel', color: '#008CBA' },
    { name: 'VPS', level: 85, icon: <SiDigitalocean />, description: 'Virtual Private Server hosting & management', color: '#0080FF' },
    { name: 'Dedicated Server', level: 90, icon: <SiCloudflare />, description: 'High-performance server management', color: '#F38020' },
    { name: 'Docker', level: 70, icon: <SiDocker />, description: 'Containerization & app deployment', color: '#2496ED' },
    { name: 'Render', level: 85, icon: <SiRender />, description: 'Reverse proxy & web server', color: '#009639' },
    { name: 'Apache', level: 75, icon: <SiApache />, description: 'Web server for hosting applications', color: '#D22128' },
  ],

  tools: [
    { name: 'VS Code', level: 90, icon: <SiVisualstudiocode />, description: 'Advanced code editing & debugging', color: '#007ACC' },
    { name: 'Github', level: 90, icon: <SiGithub />, description: 'Git enables multiple people to edit web pages simultaneously.', color: '#F05032' },
    { name: 'Git', level: 90, icon: <SiGit />, description: 'Version control & collaboration', color: '#F05032' },
    { name: 'Postman', level: 90, icon: <SiPostman />, description: 'API development & testing', color: '#FF6C37' },
    { name: 'WebStorm', level: 80, icon: <SiWebstorm />, description: 'Advanced code editor & debugging', color: '#F24E1E' },
    { name: 'Cursor', level: 90, icon: <SiVisualstudiocode />, description: 'Collaborative code editor', color: '#6C4F97' },
    { name: 'Windows', level: 90, icon: <SiWindows />, description: 'Operating system for development', color: '#00A4EF' },
    { name: 'Linux', level: 80, icon: <SiLinux />, description: 'Open-source operating system for development', color: '#000000' },
    { name: 'Figma', level: 75, icon: <SiFigma />, description: 'Inspect design elements, extract colors, spacing & layouts', color: '#F24E1E' },
    { name: 'Google Chrome', level: 90, icon: <SiGooglechrome />, description: 'Web browsing & developer tools', color: '#4285F4' },
    { name: 'Firefox', level: 90, icon: <SiFirefox />, description: 'Web browsing & debugging tools', color: '#FF7139' },
    { name: 'Microsoft Edge', level: 90, icon: <SiMicrosoftedge />, description: 'Web browsing & testing', color: '#0078D7' },
    { name: 'MongoDB Compass', level: 85, icon: <SiMongodb />, description: 'GUI for MongoDB database management', color: '#47A248' },
    { name: 'Terminal (Bash/Zsh)', level: 85, icon: <SiGnubash />, description: 'Command-line interface for development', color: '#4EAA25' },
    { name: 'NPM', level: 90, icon: <SiNpm />, description: 'JavaScript package manager', color: '#CB3837' },
    { name: 'Yarn', level: 85, icon: <SiYarn />, description: 'Fast & efficient package manager', color: '#2C8EBB' }
  ],

  aiTools: [
    { name: 'GitHub Copilot', level: 90, description: 'AI-powered code completion', color: '#000000' },
    { name: 'ChatGPT', level: 90, icon: <SiOpenai />, description: 'AI development assistant', color: '#412991' },
    { name: 'DeepSeek', level: 90, description: 'Advanced AI for code generation and reasoning', color: '#1A73E8' },
    { name: 'Gemini', level: 90, description: 'Google’s AI for coding, research, and automation', color: '#34A853' },
    { name: "Claude AI", level: 85, description: "AI for code review, documentation, and reasoning", color: "#FFB300" }
  ],  

  methodology: [
    { name: 'Clean Code', level: 90, icon: <GoCode />, description: 'SOLID principles & best practices', color: '#4B32C3' },
    { name: 'CI/CD', level: 75, icon: <SiCircleci />, description: 'Continuous Integration & Deployment', color: '#006F6A' },
    { name: 'Agile', level: 85, icon: <SiJira />, description: 'Iterative development & teamwork', color: '#0052CC' },
    { name: 'DRY', level: 90, icon: <GoCodeReview />, description: 'Avoid redundant code & improve reusability', color: '#C70039' },
    { name: 'KISS', level: 80, icon: <GoLightBulb />, description: 'Keep It Simple & Scalable', color: '#FF5733' },
    { name: 'TDD', level: 70, icon: <SiTestinglibrary />, description: 'Write tests before implementation', color: '#E34F26' },
    { name: 'YAGNI', level: 75, icon: <GoShieldCheck />, description: 'Avoid overengineering; build only what’s needed', color: '#008080' }
  ]
  
};

const SkillCard = ({ skill }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="bg-white/10 backdrop-blur-lg p-6 rounded-xl relative overflow-hidden group"
  >
    <div className="flex items-center space-x-4 mb-4">
      <span className={`text-3xl ${getIconColor(skill.name)}`}>{skill.icon}</span>
      <h3 className="text-xl font-bold text-white">{skill.name}</h3>
    </div>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${skill.level}%` }}
      className="h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
    />
    <p className="mt-4 text-gray-400 text-sm">{skill.description}</p>
    <div className="absolute top-2 right-2 bg-white/20 px-2 py-1 rounded text-sm">
      {skill.level}%
    </div>
  </motion.div>
);

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white relative">
      <div className="absolute inset-0 z-0">
      <SEO 
        title="Mahmudur Rahman - Skills"
        description="Full Stack Developer specializing in MERN stack development. Building modern web applications with React, Node.js, and MongoDB."
        image="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740488530/portfolio-banner_xywidc.jpg"
      />
        <Canvas>
          <Suspense fallback={null}>
            <OrbitControls enableZoom={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[-2, 5, 2]} intensity={1} />
            <ParticlesSphere />
          </Suspense>
        </Canvas>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-5xl font-bold text-center mb-16 mt-8 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"
        >
          Technical Skills & Tools
        </motion.h1>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.keys(skillsData).map((category) => (
            <motion.button
              key={category}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded-full ${activeCategory === category ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
            >
              {category.replace(/([A-Z])/g, ' $1').trim()}
            </motion.button>
          ))}
        </div>

        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence mode="wait">
            {skillsData[activeCategory].map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
              >
                <SkillCard skill={skill} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Skills;
